import type { FetchedTenantTypes } from "@/Utils/api/sanity/types";

import type { Customer } from "server/dwhApi/customers/types.js";

import type { ContributionMode, ContributionSliceData } from "./types";

const PALETTE = [
  "#643E3E",
  "#1D1C1C",
  "#B6BB22",
  "#E7EB5D",
  "#87874E",
  "#907474",
  "#A8A862",
];
const OTHER_COLOR = "#DDCAB9";
const TENANT_TOP_N = 7;

export const buildContributionData = ({
  mode,
  tenants,
  tenantCategories,
  t,
}: {
  mode: ContributionMode;
  tenants: Customer[];
  tenantCategories: FetchedTenantTypes | null;
  t: (key: string) => string;
}): ContributionSliceData[] => {
  const total =
    tenants.reduce((sum, t) => sum + t.totalWasteWeight.quantity, 0) || 1;

  if (mode === "category") {
    const byCategory = new Map<string, number>();
    tenants.forEach((t) =>
      byCategory.set(
        t.commercialCustomerCategory,
        (byCategory.get(t.commercialCustomerCategory) || 0) +
          t.totalWasteWeight.quantity,
      ),
    );

    return [...byCategory.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([id, waste], i) => ({
        label:
          tenantCategories?.find((c) => c.id === id)?.name ||
          id ||
          t("homePage:tenantContributionNoCategory"),
        value: waste,
        pct: (waste / total) * 100,
        color: PALETTE[i % PALETTE.length],
      }));
  }

  const sorted = [...tenants].sort(
    (a, b) => b.totalWasteWeight.quantity - a.totalWasteWeight.quantity,
  );
  const top = sorted.slice(0, TENANT_TOP_N);
  const rest = sorted.slice(TENANT_TOP_N);

  const data: ContributionSliceData[] = top.map((t, i) => ({
    label: t.customerName,
    value: t.totalWasteWeight.quantity,
    pct: (t.totalWasteWeight.quantity / total) * 100,
    color: PALETTE[i % PALETTE.length],
    isDeleted: t.isDeleted,
  }));

  if (rest.length) {
    const restWaste = rest.reduce(
      (sum, t) => sum + t.totalWasteWeight.quantity,
      0,
    );
    data.push({
      label: t("homePage:tenantContributionOtherTenants"),
      value: restWaste,
      pct: (restWaste / total) * 100,
      color: OTHER_COLOR,
    });
  }

  return data;
};
